import {
  AloftDate,
  AloftPrice,
  PairedCostRouteItem,
  PairedCostRouteItemWithQuery,
  orUndefined,
  schAlertType,
  schAloftPrice,
  schPairedCostRouteItem,
  syntaxFNMA,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getComparableDisplayName, getScopedAlertType } from '../utils/compTypeScoping.ts';
import { getSimilarComparableToSubjectPriorSaleRelationship } from './similarComparableToSubjectPriorSaleUtils.ts';

export const similarComparableToSubjectPriorSale: AlertDefinition = ({
  compSequence,
  compType,
  priorSales,
  propertyDataEntities,
  order,
  fullCompSequence,
}) => {
  const subjectPriorSale = priorSales.subject?.[0];

  if (!subjectPriorSale) return [];

  const priorSaleDate = orUndefined(AloftDate.fromIsoString)(subjectPriorSale.CloseDate);
  const priorSalePrice = orUndefined(AloftPrice.fromDollarLike)(subjectPriorSale.ClosePrice);
  if (!priorSaleDate || !priorSalePrice) return [];

  return compSequence
    .map((id, index) => {
      const propertyData = (propertyDataEntities[id] as CompPropertyDataState | undefined)
        ?.compData;

      const saleDate = orUndefined(syntaxFNMA.isoMonthToAloftDate)(
        propertyData?.editableData.transaction.saleDate,
      );
      const salePrice = orUndefined(schAloftPrice.parse)(
        propertyData?.editableData.transaction.price,
      );

      return { id, index, saleDate, salePrice };
    })
    .filter(
      (c): c is { id: string; index: number; saleDate: AloftDate; salePrice: AloftPrice } =>
        !!c.saleDate && !!c.salePrice,
    )
    .flatMap(({ id: propertyID, saleDate, salePrice }) => {
      const relationship = getSimilarComparableToSubjectPriorSaleRelationship(
        saleDate,
        salePrice,
        priorSaleDate,
        priorSalePrice,
      );
      if (!relationship) return [];

      const propertyName = getComparableDisplayName(propertyID, fullCompSequence);

      return [
        {
          type: getScopedAlertType(schAlertType.enum.similarComparableToSubjectPriorSale, compType),
          status: 'active' as const,
          severity: 'warning' as const,
          orderType: ['appraisify'] as const,
          message: `${propertyName} sold significantly ${relationship} than Subject's prior sale in a 12 month period (${propertyName}: ${salePrice.toString()}, Subject Prior Sale: ${priorSalePrice.toString()}).`,
          propertyID,
          ...(compType ? { compType } : {}),
          fixingInfo: {
            tips: 'Tips: Review the historical market relationship between the properties, and determine if the same relative positioning is still supported by current market evidence. The prior sales price relationship should remain consistent as of the Effective Date unless the Subject or the noted Comparable have changed significantly since these prior transactions.',
            pairedCostRouteItem: `${schPairedCostRouteItem.enum.features}` as PairedCostRouteItem,
            targetCellKey: `${propertyID}-editableData.transaction.price`,
            fixingMethod: ['action'] as const,
            commentAlwaysRequired: false,
          },
          triggers: getAlertTriggers({
            fixRoute: `${schPairedCostRouteItem.enum.features}` as PairedCostRouteItemWithQuery,
            order,
          }),
        },
      ];
    });
};
